'use client';

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from 'src/utils/axios';

interface UserConfigValue {
  localExtensionId?: string;
  [key: string]: unknown;
}

interface UserConfigResponse {
  key: string;
  value: UserConfigValue;
  created_at: string;
  updated_at: string;
  description?: string;
}

export function useUserConfig(configKey: string) {
  const queryClient = useQueryClient();

  // Get user config
  const {
    data: config,
    isLoading,
    error
  } = useQuery({
    queryKey: ['userConfig', configKey],
    queryFn: async (): Promise<UserConfigResponse | null> => {
      try {
        const response = await axiosInstance.get<UserConfigResponse>(`/v1/user/config/${configKey}`);
        return response;
      } catch (error: unknown) {
        if ((error as { response?: { status?: number } }).response?.status === 404) {
          return null; // Config doesn't exist yet
        }
        throw error;
      }
    },
    refetchOnWindowFocus: false
  });

  // Update user config
  const updateConfig = useMutation({
    mutationFn: async ({ value, description }: { value: UserConfigValue; description?: string }) => {
      const response = await axiosInstance.put<{ success: boolean; message: string }>(`/v1/user/config/${configKey}`, {
        value,
        description
      });
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfig', configKey] });
    }
  });

  // Delete user config
  const deleteConfig = useMutation({
    mutationFn: async () => {
      const response = await axiosInstance.delete<{ success: boolean; message: string }>(`/v1/user/config/${configKey}`);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userConfig', configKey] });
    }
  });

  // Get effective extension ID (user config or env variable)
  const getEffectiveExtensionId = useCallback(() => {
    const userExtensionId = config?.value?.localExtensionId;
    return userExtensionId || process.env.NEXT_PUBLIC_EXTENSION_ID || '';
  }, [config?.value?.localExtensionId]);

  return {
    config,
    isLoading,
    error,
    updateConfig,
    deleteConfig,
    getEffectiveExtensionId
  };
}

// Specific hook for extension settings
export function useExtensionConfig() {
  return useUserConfig('extensionSettings');
}